<template>
  <modal
    class="share-modal"
    v-if="isOpenModal"
    @close="closeModal"
  >
    <share-modal-header @close="closeModal"/>
    <div class="content">
      <share-modal-email-list
        placeholder="Recipients e-mails"
        :is-invalid="showErrorValidate"
        v-model="emails"
      />
      <div class="share-validate_error" :class="{'share-validate_error--show': showErrorValidate}">
        <span v-if="showErrorValidate">{{ errorValidateMsg }}</span>
        <span v-else aria-hidden="true">Fake text</span>
      </div>
      <share-modal-message v-model="message" />
    </div>
    <info-box
      v-if="showErrorInfo"
      class="modal-error-info"
      info-title="Experiment could not be shared."
      info-text="Due to technical reasons, we were unable to share the experiment. Please try again."
    />
    <share-modal-footer
      @on-share="onShareWorkflow"
      @on-copy="onCopyLink"
    />
    <sending-form
      v-if="sendingForm.show"
      class="share-modal_sending"
      :loading="sendingForm.loader"
      success-title="Experiment has been shared!"
      success-body=""
      @backToWorkflow="closeModal"
    />
  </modal>
</template>

<script>
import copyToClipboard from '@/mixins/copyToClipboard';
import { validateEmail } from '@/util/validateEmail.js';
import { showGlobalBanner } from '@/services/global-banner.js';
import apiRequest, { http_headers_azure } from '@/services/api-request.js';
import callToApiBackend from '@/components/workflow/mixins/callToApiBackend';
import InfoBox from "@/shared/InfoBox";
import ShareModalFooter from "./ShareModalFooter";
import ShareModalHeader from "./ShareModalHeader";
import ShareModalEmailList from "./ShareModalEmailList";
import SendingForm from "../requestsModalComponents/SendingForm";
import ShareModalMessage from "./ShareModalMessage";
import { mapState, mapGetters, mapMutations } from "vuex";
import DOMPurify from 'dompurify';

const validateErrEmptyList = 'Please provide at least one e-mail address to share the workflow';
const validateErrInvalidMail = 'Please provide valid e-mail addresses.';

export default {
  name: 'ShareModal',
  components: {ShareModalMessage, SendingForm, InfoBox, ShareModalEmailList, ShareModalHeader, ShareModalFooter},
  mixins: [copyToClipboard, callToApiBackend],
  created() {
    this.createShareWorkflowLink();
  },
  data() {
    return {
      shareableLink: '',
      emails: [],
      message: '',
      errorValidateMsg: '',
      showErrorInfo: false,
      sendingForm: {
        show: false,
        loader: true,
      }
    };
  },
  computed: {
    ...mapState('featureShare', {
      wfInfo: state => state.wfInfo,
    }),
    isOpenModal() {
      return !!this.shareableLink
    },
    showErrorValidate() {
      return !!this.errorValidateMsg
    },
    workflowTitle() {
      return this.$store.state.workflowHeadline || this.$store.state.workflowDefaultHeadline;
    },
    sharedEndpoint() {
      return this.$store.state.sharedEndpoint;
    }
  },
  watch: {
    emails() {
      if(this.showErrorValidate) this.errorValidateMsg = '';
    },
  },
  methods: {
    ...mapMutations('featureShare', [
      'closeModal',
    ]),
    createShareWorkflowLink() {
      let url = this.sharedEndpoint;
      apiRequest(url, 'post', this.wfInfo, null, {
        withCredentials: true,
      })
        .then((data) => {
          this.shareableLink = `${window.location.origin}/workflow-configurator/shared/${data.title}/${data.id}`;
        })
        .catch(() => {
          showGlobalBanner(this, {
            title: 'Experiment could not be shared.',
            body: 'Due to technical reasons, we were unable to share the experiment. Please try again.',
            btn: 'dismiss',
          });
          this.closeModal();
        });
    },
    sendEmails() {
      const url = `${this.sharedEndpoint}/send_email`;
      const email_msg = this.prepareMessage(this.message);
      const option = {
        recipients: this.emails,
        email_msg,
        shared_link: this.shareableLink,
        title: this.workflowTitle,
      };
      this.sendingForm.show = true;
      apiRequest(url, 'post', option, http_headers_azure, {
        withCredentials: true,
      })
        .then(() => {
          this.sendingForm.loader = false
        })
        .catch(() => {
          this.sendingForm.show = false;
          this.showErrorInfo = true;
        });
    },
    validEmailList() {
      return this.emails.length && this.emails.every((mail) => validateEmail(mail));
    },
    prepareMessage(txt) {
      return DOMPurify.sanitize(txt.split('\n').join('<br>'));
    },
    onShareWorkflow() {
      this.showErrorInfo = false;
      if(this.validEmailList()) this.sendEmails();
      else {
        if(!this.emails.length) this.errorValidateMsg = validateErrEmptyList;
        else this.errorValidateMsg = validateErrInvalidMail;
      }
    },
    onCopyLink() {
      this.copyToClipboard(this.shareableLink)
    }
  },
};
</script>

<style lang="scss" scoped>
.content {
  @include flexColumn;
  position: relative;
}
.share-modal {
  ::v-deep {
    .modal-container {
      padding: 18px 25px 8px;
    }
  }
}
.share-validate_error {
  font-size: 13px;
  color: $color-red;
  padding: 0 8px;
  margin-bottom: 9px;
  opacity: 0;
  visibility: hidden;
}
.share-validate_error--show {
  opacity: 1;
  visibility: visible;
}
.modal-error-info {
  margin-bottom: 26px;
}
.share-modal {
  ::v-deep {
    .modal-container {
      position: relative;
    }
  }
}
.share-modal_sending {
  position: absolute;
  z-index: 10;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #fff;
  margin: 0;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
